import { FormGroup, InputLabel } from "@mui/material";
import React, { useEffect, useState } from "react";
import { SelectOption, SimpleSelect } from "../../../common/components/input/Select";
import { NetworkRequest } from "../../../common/models";
import {
  LoadingPageWrapper,
  StandardPageWrapper,
} from "../../components/layout/StandardPageWrapper";
import { EmailLookupForm } from "../../components/records/EmailLookupForm";
import { RequestLookupForm } from "../../components/records/RequestLookupForm";
import { usePrimaryOrganization } from "../../hooks";
import { useQuery } from "../../hooks/useQuery";
import { NameLookupForm } from "../../components/records/NameLookupForm";
import { RequestIdLookupForm } from "../../components/records/RequestIdLookupForm";
import { PageHeader } from "../../components/layout/header/Header";
import { RequestInboxTabs } from "./RequestListingPage";
import { useSelfOrganizationUser } from "../../hooks/useOrganization";

type LookupType = "email" | "name" | "request" | "requestId";

const LookupFormOptions: SelectOption[] = [
  {
    label: "Consumer Email",
    value: "email",
  },
  {
    label: "Consumer Name",
    value: "name",
  },
  {
    label: "Request Type",
    value: "request",
  },
  {
    label: "Request ID",
    value: "requestId",
  },
];

export function RecordLookupPage() {
  const params = useQuery<{ lookupType: LookupType }>();
  const [everRendered, setEverRendered] = useState(false);
  const [lookupType, setLookupType] = useState<LookupType>(params.lookupType ?? "email");
  const [org, orgRequest] = usePrimaryOrganization();

  const currentOrgUser = useSelfOrganizationUser();
  const isHrUser = currentOrgUser?.role === "HR";

  let LookupFormComponent = null;
  switch (lookupType) {
    case "email":
      LookupFormComponent = EmailLookupForm;
      break;
    case "name":
      LookupFormComponent = NameLookupForm;
      break;
    case "requestId":
      LookupFormComponent = RequestIdLookupForm;
      break;
    case "request":
    default:
      // Hide the "Request Type" option for HR users since most request types aren't relevant to HR users
      LookupFormComponent = isHrUser ? RequestIdLookupForm : RequestLookupForm;
  }

  useEffect(() => {
    if (NetworkRequest.areFinished(orgRequest)) {
      setEverRendered(true);
    }
  }, [orgRequest]);

  // Don't load the page until we have the user's role, since we use their role to determine access permissions for filtering HR requests
  if (!everRendered || !currentOrgUser) {
    return <LoadingPageWrapper />;
  }

  return (
    <StandardPageWrapper fullWidth>
      <PageHeader titleContent="Record Lookup" />

      <div style={{ maxWidth: "1214px", paddingRight: "32px" }}>
        <div className="tw-boundary">
          <RequestInboxTabs selectedTabKey="record-lookup" />
        </div>

        <div className="mt-lg">
          <FormGroup className="mb-md">
            <InputLabel className="mb-sm">Lookup By&hellip;</InputLabel>

            <SimpleSelect
              // Hide the "Request Type" option for HR users since most request types aren't relevant to HR users
              options={
                isHrUser
                  ? LookupFormOptions.filter((option) => option.value !== "request")
                  : LookupFormOptions
              }
              value={lookupType}
              onChange={(e) => setLookupType(e.target.value as LookupType)}
            />
          </FormGroup>

          <LookupFormComponent org={org} limitToHrRequests={isHrUser ?? false} />
        </div>
      </div>
    </StandardPageWrapper>
  );
}
